import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Eye, Inbox, Search, MessageCircle, BellRing, Printer, FileDown,
  Copy, AlertTriangle, StickyNote, RefreshCw, Archive, Trash2, FlaskConical,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtBRL, buildWhatsAppUrl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useSettings } from "@/context/SettingsContext";

const STATUS = {
  aguardando_confirmacao: { label: "Aguardando confirmação", cls: "bg-amber-50 text-amber-700" },
  confirmado: { label: "Confirmado", cls: "bg-blue-50 text-brand" },
  em_separacao: { label: "Em separação", cls: "bg-violet-50 text-violet-700" },
  saiu_para_entrega: { label: "Saiu para entrega", cls: "bg-indigo-50 text-indigo-700" },
  entregue: { label: "Entregue", cls: "bg-green-50 text-green-700" },
  cancelamento_solicitado: { label: "Cancelamento solicitado", cls: "bg-orange-50 text-orange-700" },
  cancelado: { label: "Cancelado", cls: "bg-red-50 text-red-600" },
};

const PAYMENT_FILTERS = [
  { id: "", label: "Todos os pagamentos" },
  { id: "pix", label: "Pix" },
  { id: "dinheiro", label: "Dinheiro" },
  { id: "debito", label: "Débito" },
  { id: "credito", label: "Crédito" },
  { id: "combinado", label: "Combinado" },
];

const fmtDate = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");
const fmtDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
};
const fmtCpf = (cpf) => {
  const d = String(cpf || "").replace(/\D/g, "");
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : cpf || "—";
};
const lastNotif = (o) => (o?.notifications?.length ? o.notifications[o.notifications.length - 1] : null);
// falha de verdade = envio tentado e com erro (desativadas e pendentes não são falha)
const notifFailed = (o) => {
  const n = lastNotif(o);
  if (!n) return false;
  if (n.status) return n.status === "falha";
  return !n.ok && !(n.detail || "").startsWith("Notificações desativadas") && !(n.detail || "").startsWith("Pendente:");
};
const unitOf = (o, i) =>
  o.payment_method === "credito" || o.payment_method === "debito"
    ? i.unit_card ?? i.unit_cash ?? i.unit_price ?? 0
    : i.unit_cash ?? i.unit_price ?? 0;

// ---------- modelo profissional de impressão (resumo interno, não é NF)
const printOrder = (o) => {
  const rows = o.items
    .map((i, idx) => {
      const unit = unitOf(o, i);
      return `<tr>
        <td>${idx + 1}</td>
        <td>${i.name}${i.model ? ` (${i.model})` : ""}</td>
        <td>${i.sku || "—"}</td>
        <td>${i.color || "—"}</td>
        <td style="text-align:center">${i.qty}</td>
        <td style="text-align:right">${fmtBRL(unit)}</td>
        <td style="text-align:right">${fmtBRL(unit * i.qty)}</td>
      </tr>`;
    })
    .join("");
  const combined = (o.combined || [])
    .map((c) => `<p>• ${c.method === "credito" ? "Cartão de crédito" : c.method === "debito" ? "Cartão de débito" : c.method === "pix" ? "Pix" : "Dinheiro"}: <strong>${fmtBRL(c.amount)}</strong></p>`)
    .join("");
  const installments =
    o.installments > 1 && o.installment_values?.length
      ? `<p>Parcelas: <strong>${o.installments}x de ${fmtBRL(o.installment_values[0])}</strong></p>`
      : "";
  const notes = (o.internal_notes || [])
    .map((n) => `<p class="small">• [${fmtDateTime(n.at)} — ${n.by}] ${n.text}</p>`)
    .join("");

  const w = window.open("", "_blank", "width=820,height=940");
  if (!w) {
    toast.error("Permita pop-ups para imprimir o pedido.");
    return;
  }
  w.document.write(`<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8">
<title>Pedido ${o.order_number} — HG Móveis</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color:#0f172a; padding:32px; font-size:13px; }
  .head { display:flex; align-items:center; gap:16px; border-bottom:3px solid #0B5394; padding-bottom:14px; }
  .head img { width:64px; height:64px; object-fit:contain; }
  .head h1 { font-size:22px; color:#0B5394; }
  .head p { color:#475569; font-size:12px; margin-top:2px; }
  .doc { text-align:right; margin-left:auto; }
  .doc .num { font-size:20px; font-weight:bold; }
  .doc .st { display:inline-block; margin-top:4px; background:#fef3c7; color:#92400e; font-weight:bold; font-size:11px; padding:3px 10px; border-radius:999px; text-transform:uppercase; }
  h2 { background:#0B5394; color:#fff; font-size:12px; text-transform:uppercase; letter-spacing:.06em; padding:6px 10px; margin:18px 0 8px; border-radius:4px; }
  .grid { display:grid; grid-template-columns:1fr 1fr; gap:4px 24px; }
  .grid p, .cols p { padding:2px 0; }
  .lbl { color:#64748b; font-size:11px; }
  table { width:100%; border-collapse:collapse; }
  th, td { border:1px solid #cbd5e1; padding:6px 8px; font-size:12px; }
  th { background:#f1f5f9; text-align:left; }
  .totais { margin-top:10px; margin-left:auto; width:280px; }
  .totais div { display:flex; justify-content:space-between; padding:3px 0; }
  .totais .total { font-size:16px; font-weight:bold; border-top:2px solid #0f172a; margin-top:4px; padding-top:6px; }
  .small { font-size:11px; color:#475569; }
  .aviso { margin-top:24px; border:1px dashed #94a3b8; border-radius:6px; padding:10px; font-size:11px; color:#475569; text-align:center; }
  .assinaturas { display:flex; justify-content:space-between; margin-top:56px; }
  .assinaturas div { border-top:1px solid #0f172a; width:40%; text-align:center; padding-top:6px; font-size:11px; color:#475569; }
</style></head><body>
  <div class="head">
    <img src="${window.location.origin}/assets/logo.png" alt="HG Móveis" />
    <div>
      <h1>HG Móveis</h1>
      <p>Resumo interno do pedido — faturamento e entrega</p>
      <p>Documento interno. Não é nota fiscal eletrônica.</p>
    </div>
    <div class="doc">
      <div class="num">${o.order_number || "#" + o.id}</div>
      <div class="st">${STATUS[o.status]?.label || o.status}</div>
    </div>
  </div>

  <h2>Pedido</h2>
  <div class="grid">
    <p><span class="lbl">Data/hora:</span> ${fmtDateTime(o.created_at)}</p>
    <p><span class="lbl">Entrega solicitada:</span> ${fmtDate(o.delivery_date)}</p>
    <p><span class="lbl">Região:</span> ${o.region_name || "—"}</p>
  </div>

  <h2>Cliente</h2>
  <div class="grid">
    <p><span class="lbl">Nome:</span> ${o.customer.name}</p>
    <p><span class="lbl">CPF:</span> ${fmtCpf(o.customer.cpf)}</p>
    <p><span class="lbl">Telefone/WhatsApp:</span> ${o.customer.phone}</p>
    <p><span class="lbl">E-mail:</span> ${o.customer.email || "—"}</p>
  </div>

  <h2>Endereço de entrega</h2>
  <div class="cols">
    <p>${o.customer.street}, ${o.customer.number}${o.customer.complement ? ` — ${o.customer.complement}` : ""}</p>
    <p>${o.customer.neighborhood} — ${o.customer.city}/${o.customer.state} — CEP ${o.customer.cep}</p>
    ${o.customer.reference ? `<p><span class="lbl">Referência:</span> ${o.customer.reference}</p>` : ""}
    ${o.customer.note ? `<p><span class="lbl">Observações do cliente:</span> ${o.customer.note}</p>` : ""}
  </div>

  <h2>Produtos</h2>
  <table>
    <thead><tr><th>#</th><th>Produto</th><th>Código</th><th>Cor</th><th>Qtd</th><th style="text-align:right">Unitário</th><th style="text-align:right">Subtotal</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>

  <h2>Pagamento na entrega</h2>
  <p><strong>${o.payment_label || o.payment_method}</strong></p>
  ${combined}${installments}
  <div class="totais">
    <div><span>Subtotal dos produtos</span><span>${fmtBRL(o.subtotal)}</span></div>
    <div><span>Frete (${o.region_name || "região atendida"})</span><span>${fmtBRL(o.delivery_fee)}</span></div>
    <div class="total"><span>TOTAL DO PEDIDO</span><span>${fmtBRL(o.total)}</span></div>
  </div>

  ${notes ? `<h2>Observações internas</h2>${notes}` : ""}

  <div class="assinaturas">
    <div>Conferido por (faturamento)</div>
    <div>Recebido por (entrega)</div>
  </div>
  <div class="aviso">Este documento é um resumo interno do pedido para faturamento e entrega. Não substitui a nota fiscal eletrônica.</div>
</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 400);
};

// ---------- texto para copiar para o sistema interno
const orderToText = (o) => {
  const lines = [
    `PEDIDO ${o.order_number || o.id} — HG MÓVEIS (resumo interno, não é NF)`,
    `Data: ${fmtDateTime(o.created_at)} | Status: ${STATUS[o.status]?.label || o.status}`,
    "",
    `CLIENTE: ${o.customer.name}`,
    `CPF: ${fmtCpf(o.customer.cpf)} | Telefone/WhatsApp: ${o.customer.phone}${o.customer.email ? ` | E-mail: ${o.customer.email}` : ""}`,
    `ENDEREÇO: ${o.customer.street}, ${o.customer.number}${o.customer.complement ? ` — ${o.customer.complement}` : ""} — ${o.customer.neighborhood} — ${o.customer.city}/${o.customer.state} — CEP ${o.customer.cep}`,
  ];
  if (o.customer.reference) lines.push(`Referência: ${o.customer.reference}`);
  if (o.customer.note) lines.push(`Observações do cliente: ${o.customer.note}`);
  lines.push("", "ITENS:");
  o.items.forEach((i, idx) => {
    const unit = unitOf(o, i);
    lines.push(`${idx + 1}. ${i.name}${i.model ? ` (${i.model})` : ""}${i.sku ? ` [${i.sku}]` : ""}${i.color ? ` — Cor: ${i.color}` : ""} — ${i.qty}x ${fmtBRL(unit)} = ${fmtBRL(unit * i.qty)}`);
  });
  lines.push("", `PAGAMENTO NA ENTREGA: ${o.payment_label || o.payment_method}`);
  (o.combined || []).forEach((c) => {
    lines.push(`  • ${c.method === "credito" ? "Cartão de crédito" : c.method === "debito" ? "Cartão de débito" : c.method === "pix" ? "Pix" : "Dinheiro"}: ${fmtBRL(c.amount)}`);
  });
  if (o.installments > 1 && o.installment_values?.length) {
    lines.push(`Parcelas: ${o.installments}x de ${fmtBRL(o.installment_values[0])}`);
  }
  lines.push(
    `Subtotal: ${fmtBRL(o.subtotal)} | Frete (${o.region_name || "região atendida"}): ${fmtBRL(o.delivery_fee)}`,
    `TOTAL DO PEDIDO: ${fmtBRL(o.total)}`,
    `Entrega solicitada: ${fmtDate(o.delivery_date)}`
  );
  return lines.join("\n");
};

export default function AdminOrders() {
  const settings = useSettings();
  const [params] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("q") || "");
  const [filters, setFilters] = useState({ status: "", payment: "", region: "", date_from: "", date_to: "" });
  const [quickTab, setQuickTab] = useState(params.get("q") ? "todos" : "ativos"); // link do e-mail abre em "todos"
  const [selected, setSelected] = useState([]); // ids de pedidos de teste selecionados
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [detail, setDetail] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [pendingCancel, setPendingCancel] = useState(null); // {orderId, label}
  const [cancelReason, setCancelReason] = useState("");
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null); // pedido de teste a excluir
  const [deleting, setDeleting] = useState(false);

  const buildParams = (q = search) => {
    const params = { archived: quickTab === "arquivados" ? "only" : quickTab === "todos" ? "all" : "false" };
    if (quickTab === "teste") params.test = "only";
    if (quickTab === "cancelados") params.status = "cancelado";
    else if (filters.status) params.status = filters.status;
    if (q) params.search = q;
    if (filters.payment) params.payment = filters.payment;
    if (filters.region) params.region = filters.region;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    return params;
  };

  const load = (q = search, silent = false) => {
    if (!silent) setLoading(true);
    api
      .get("/admin/orders", { params: buildParams(q) })
      .then((r) => {
        setOrders(r.data);
        setSelected([]);
        setLastUpdate(new Date());
      })
      .finally(() => !silent && setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(search); }, [quickTab, filters]);

  // atualização automática da listagem a cada 30s
  useEffect(() => {
    const t = setInterval(() => load(search, true), 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, quickTab, filters]);

  // "ativos" = não arquivados e não cancelados (filtro local sobre a lista carregada)
  const visibleOrders = quickTab === "ativos" ? orders.filter((o) => o.status !== "cancelado") : orders;
  const visibleTotal = visibleOrders.reduce((s, o) => s + (o.total || 0), 0);
  const testSelected = selected.filter((id) => orders.find((o) => o.id === id && o.is_test));

  const toggleSelect = (o) => {
    if (!o.is_test) return;
    setSelected((prev) => (prev.includes(o.id) ? prev.filter((x) => x !== o.id) : [...prev, o.id]));
  };
  const visibleTestIds = visibleOrders.filter((o) => o.is_test).map((o) => o.id);
  const allTestSelected = visibleTestIds.length > 0 && visibleTestIds.every((id) => selected.includes(id));

  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/admin/orders/bulk-delete", { ids: testSelected });
      setOrders((prev) => prev.filter((x) => !testSelected.includes(x.id)));
      if (detail && testSelected.includes(detail.id)) setDetail(null);
      setSelected([]);
      setConfirmBulk(false);
      toast.success(`${data.deleted} pedido(s) de teste excluído(s) (cópias de auditoria preservadas).`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkDeleting(false);
    }
  };

  const updateStatus = async (orderId, status, reason = "") => {
    try {
      const { data } = await api.patch(`/admin/orders/${orderId}/status`, { status, reason });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? data : o)));
      if (detail?.id === orderId) setDetail(data);
      toast.success("Status atualizado e registrado no histórico!");
      return data;
    } catch (err) {
      toast.error(formatApiError(err));
      return null;
    }
  };

  // cancelamento pelo admin exige motivo e pode avisar o cliente no WhatsApp
  const confirmCancel = async () => {
    if (!pendingCancel) return;
    const updated = await updateStatus(pendingCancel.id, "cancelado", cancelReason);
    if (updated && notifyCustomer) {
      const phone = (updated.customer.phone || "").replace(/\D/g, "");
      const msg = `Olá ${updated.customer.name}! Seu pedido ${updated.order_number || ""} na HG Móveis foi CANCELADO.${cancelReason ? ` Motivo: ${cancelReason}.` : ""} Qualquer dúvida, estamos à disposição.`;
      window.open(`https://wa.me/55${phone}?text=${encodeURIComponent(msg)}`, "_blank");
    }
    setPendingCancel(null);
    setCancelReason("");
  };

  const toggleArchive = async (o) => {
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/archive`, { archived: !o.archived });
      setOrders((prev) =>
        prev
          .map((x) => (x.id === o.id ? data : x))
          .filter((x) => quickTab === "todos" || quickTab === "arquivados" || !x.archived)
          .filter((x) => quickTab !== "arquivados" || x.archived)
      );
      if (detail?.id === o.id) setDetail(data);
      toast.success(data.archived ? "Pedido arquivado (nada foi apagado)." : "Pedido desarquivado.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleTest = async (o) => {
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/test`, { is_test: !o.is_test });
      setOrders((prev) => prev.map((x) => (x.id === o.id ? data : x)));
      if (detail?.id === o.id) setDetail(data);
      toast.success(data.is_test ? "Pedido marcado como TESTE." : "Pedido desmarcado como teste.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteTestOrder = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/orders/${confirmDelete.id}`);
      setOrders((prev) => prev.filter((x) => x.id !== confirmDelete.id));
      if (detail?.id === confirmDelete.id) setDetail(null);
      toast.success(`Pedido de teste ${confirmDelete.order_number || ""} excluído (cópia de auditoria preservada).`);
      setConfirmDelete(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const retryNotify = async (orderId) => {
    try {
      const entry = await api.post(`/admin/orders/${orderId}/notify`);
      toast[entry.data.ok ? "success" : "info"](
        entry.data.ok ? "Notificação enviada!" : `Notificação: ${entry.data.detail}`
      );
      load();
      const { data } = await api.get("/admin/orders", { params: {} });
      const updated = data.find((o) => o.id === orderId);
      if (updated) setDetail(updated);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyOrder = async (o) => {
    try {
      await navigator.clipboard.writeText(orderToText(o));
      toast.success("Dados copiados! Cole no sistema interno.");
    } catch {
      toast.error("Não foi possível copiar. Selecione e copie manualmente.");
    }
  };

  const addNote = async () => {
    if (!noteText.trim() || !detail) return;
    setSavingNote(true);
    try {
      const { data } = await api.post(`/admin/orders/${detail.id}/notes`, { text: noteText.trim() });
      setDetail(data);
      setOrders((prev) => prev.map((o) => (o.id === data.id ? data : o)));
      setNoteText("");
      toast.success("Observação registrada!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Pedidos Recebidos</h1>
          <p className="text-sm text-gray-500 mt-1">
            Todos os dados para cadastrar a venda, faturar e organizar a entrega
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Nº, nome ou telefone"
              className="pl-9 w-64 rounded-full"
              data-testid="orders-search"
            />
          </div>
          <Button variant="outline" size="icon" onClick={() => load()} title="Atualizar lista" className="rounded-full" data-testid="orders-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* abas rápidas de organização */}
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="orders-quick-tabs">
        {[
          ["ativos", "Pedidos ativos"],
          ["todos", "Todos os pedidos"],
          ["cancelados", "Cancelados"],
          ["arquivados", "Arquivados"],
          ["teste", "Pedidos de teste"],
        ].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setQuickTab(id)}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
              quickTab === id ? "bg-brand text-white" : "bg-white text-gray-600 border border-gray-200 hover:border-brand/40"
            }`}
            data-testid={`orders-tab-${id}`}
          >
            {label}
          </button>
        ))}
        <span className="text-xs text-slate-500 ml-1" data-testid="orders-summary">
          {visibleOrders.length} pedido(s) • {fmtBRL(visibleTotal)}
        </span>
        {lastUpdate && (
          <span className="text-[11px] text-slate-400 ml-auto" data-testid="orders-last-update">
            Atualizado às {lastUpdate.toLocaleTimeString("pt-BR")} (auto a cada 30s)
          </span>
        )}
      </div>

      {/* barra de ações em lote (somente pedidos de teste) */}
      {testSelected.length > 0 && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="bulk-bar">
          <p className="text-sm text-red-800 font-medium">
            {testSelected.length} pedido(s) de teste selecionado(s)
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelected([])} data-testid="bulk-clear">
              Limpar seleção
            </Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => setConfirmBulk(true)} data-testid="bulk-delete-open">
              <Trash2 className="w-4 h-4" /> Excluir selecionados
            </Button>
          </div>
        </div>
      )}

      {/* filtros */}
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="orders-filters">
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}>
          <SelectTrigger className="h-9 w-[200px] text-xs rounded-full" disabled={quickTab === "cancelados"} data-testid="filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            {Object.entries(STATUS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.payment || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, payment: v === "all" ? "" : v }))}>
          <SelectTrigger className="h-9 w-[170px] text-xs rounded-full" data-testid="filter-payment">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAYMENT_FILTERS.map((p) => (
              <SelectItem key={p.id || "all"} value={p.id || "all"}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={filters.region}
          onChange={(e) => setFilters((f) => ({ ...f, region: e.target.value }))}
          placeholder="Região"
          className="h-9 w-40 text-xs rounded-full"
          data-testid="filter-region"
        />
        <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} className="h-9 w-[150px] text-xs rounded-full" data-testid="filter-date-from" />
        <Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} className="h-9 w-[150px] text-xs rounded-full" data-testid="filter-date-to" />
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="p-4 w-10">
                <input
                  type="checkbox"
                  checked={allTestSelected}
                  onChange={() => setSelected(allTestSelected ? [] : visibleTestIds)}
                  disabled={visibleTestIds.length === 0}
                  title="Selecionar todos os pedidos de teste visíveis"
                  className="accent-[#D71920]"
                  data-testid="select-all-tests"
                />
              </th>
              <th className="p-4">Pedido</th>
              <th className="p-4">Cliente</th>
              <th className="p-4">Entrega</th>
              <th className="p-4">Pagamento</th>
              <th className="p-4">Total</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Detalhes</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-8 text-center text-slate-400">Carregando...</td></tr>
            ) : visibleOrders.length === 0 ? (
              <tr>
                <td colSpan={8} className="p-12 text-center text-slate-400">
                  <Inbox className="w-10 h-10 mx-auto text-slate-200" />
                  <p className="mt-2">Nenhum pedido encontrado.</p>
                </td>
              </tr>
            ) : (
              visibleOrders.map((o) => {
                const failed = notifFailed(o);
                const notif = lastNotif(o);
                return (
                  <tr key={o.id} className={`border-b border-slate-50 hover:bg-slate-50/60 ${selected.includes(o.id) ? "bg-red-50/40" : ""}`} data-testid={`admin-order-row-${o.id}`}>
                    <td className="p-4">
                      <input
                        type="checkbox"
                        checked={selected.includes(o.id)}
                        onChange={() => toggleSelect(o)}
                        disabled={!o.is_test}
                        title={o.is_test ? "Selecionar para exclusão em lote" : "Somente pedidos de teste podem ser excluídos"}
                        className="accent-[#D71920]"
                        data-testid={`order-select-${o.id}`}
                      />
                    </td>
                    <td className="p-4">
                      <p className="font-semibold text-gray-900 flex items-center gap-1.5 flex-wrap">
                        {o.order_number || `#${o.id}`}
                        {!!o.is_test && (
                          <span className="text-[9px] font-bold uppercase bg-purple-100 text-purple-700 rounded-full px-1.5 py-0.5" data-testid={`order-test-pill-${o.id}`}>
                            Teste
                          </span>
                        )}
                        {!!o.archived && (
                          <span className="text-[9px] font-bold uppercase bg-slate-100 text-slate-500 rounded-full px-1.5 py-0.5">
                            Arquivado
                          </span>
                        )}
                        {failed && (
                          <span title={`Falha na notificação: ${notif.detail}`} data-testid={`order-notif-failed-${o.id}`}>
                            <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-slate-400">{fmtDateTime(o.created_at)}</p>
                    </td>
                    <td className="p-4">
                      <p className="font-medium text-gray-800">{o.customer.name}</p>
                      <p className="text-xs text-slate-400">{o.customer.phone}</p>
                    </td>
                    <td className="p-4">
                      <p className="font-medium text-gray-800">{fmtDate(o.delivery_date)}</p>
                      <p className="text-xs text-slate-400">{o.customer.city} — {o.customer.neighborhood}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-xs font-medium text-slate-600">{o.payment_label || o.payment_method}</p>
                      {o.installments > 1 && <p className="text-xs text-slate-400">{o.installments}x</p>}
                    </td>
                    <td className="p-4 font-semibold text-gray-900">{fmtBRL(o.total)}</td>
                    <td className="p-4">
                      <Select
                        value={o.status}
                        onValueChange={(v) =>
                          v === "cancelado"
                            ? setPendingCancel({ id: o.id, label: o.order_number || `#${o.id}` })
                            : updateStatus(o.id, v)
                        }
                      >
                        <SelectTrigger className={`h-8 w-[190px] text-xs font-semibold border-0 ${STATUS[o.status]?.cls}`} data-testid={`order-status-${o.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <Button variant="ghost" size="icon" onClick={() => setDetail(o)} title="Ver detalhes" data-testid={`order-detail-${o.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => toggleArchive(o)}
                          title={o.archived ? "Desarquivar pedido" : "Arquivar pedido (preserva o histórico)"}
                          data-testid={`order-archive-icon-${o.id}`}
                        >
                          <Archive className={`w-4 h-4 ${o.archived ? "text-brand" : "text-slate-400"}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => (o.is_test ? setConfirmDelete(o) : toast.info("Pedidos reais não podem ser excluídos — use Arquivar para organizar o painel."))}
                          title={o.is_test ? "Excluir pedido de teste" : "Só pedidos de teste podem ser excluídos"}
                          data-testid={`order-delete-icon-${o.id}`}
                        >
                          <Trash2 className={`w-4 h-4 ${o.is_test ? "text-red-500" : "text-slate-300"}`} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
          <DialogHeader>
            <DialogTitle>Pedido {detail?.order_number || `#${detail?.id}`}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-5 text-sm">
              {/* ações do pedido */}
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" size="sm" onClick={() => printOrder(detail)} data-testid="order-print">
                  <Printer className="w-4 h-4" /> Imprimir
                </Button>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${detail.id}/pdf`}
                  className="block"
                >
                  <Button variant="outline" size="sm" className="w-full" data-testid="order-pdf">
                    <FileDown className="w-4 h-4" /> Gerar PDF
                  </Button>
                </a>
                <Button variant="outline" size="sm" onClick={() => copyOrder(detail)} data-testid="order-copy">
                  <Copy className="w-4 h-4" /> Copiar dados
                </Button>
              </div>

              {/* organização: arquivar / teste / excluir */}
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" size="sm" onClick={() => toggleArchive(detail)} data-testid="order-archive">
                  <Archive className="w-4 h-4" /> {detail.archived ? "Desarquivar" : "Arquivar"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleTest(detail)}
                  className={detail.is_test ? "border-purple-300 text-purple-700" : ""}
                  data-testid="order-test-toggle"
                >
                  <FlaskConical className="w-4 h-4" /> {detail.is_test ? "É teste ✓" : "Marcar teste"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!detail.is_test}
                  title={detail.is_test ? "Excluir este pedido de teste" : "Só pedidos marcados como TESTE podem ser excluídos — pedidos reais são arquivados"}
                  onClick={() => setConfirmDelete(detail)}
                  className="border-red-200 text-red-600 disabled:opacity-40"
                  data-testid="order-delete"
                >
                  <Trash2 className="w-4 h-4" /> Excluir teste
                </Button>
              </div>

              {/* cliente e endereço */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="font-semibold text-gray-900 mb-1">Cliente</p>
                  <p>{detail.customer.name}</p>
                  <p className="text-slate-500 text-xs mt-1">
                    Tel: {detail.customer.phone}
                    {detail.customer.email && ` • ${detail.customer.email}`}
                  </p>
                  {detail.customer.cpf && (
                    <p className="text-slate-500 text-xs" data-testid="order-cpf">
                      CPF: {fmtCpf(detail.customer.cpf)}
                    </p>
                  )}
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="font-semibold text-gray-900 mb-1">Entrega — {fmtDate(detail.delivery_date)}</p>
                  <p>
                    {detail.customer.street}, {detail.customer.number}
                    {detail.customer.complement ? ` — ${detail.customer.complement}` : ""}
                  </p>
                  <p>{detail.customer.neighborhood} — {detail.customer.city}/{detail.customer.state}</p>
                  <p className="text-slate-500 text-xs mt-1">CEP {detail.customer.cep}</p>
                  {detail.customer.reference && (
                    <p className="text-slate-500 text-xs">Ref: {detail.customer.reference}</p>
                  )}
                </div>
              </div>

              {detail.customer.note && (
                <p className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3">Obs do cliente: {detail.customer.note}</p>
              )}

              {/* itens */}
              <ul className="divide-y divide-slate-100">
                {detail.items.map((i, idx) => {
                  const unit = unitOf(detail, i);
                  return (
                    <li key={idx} className="py-2 flex items-center gap-3">
                      {i.image && <img src={i.image} alt="" className="w-10 h-10 rounded-lg object-cover bg-slate-100" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-800">{i.qty}x {i.name}{i.color ? ` (${i.color})` : ""}</p>
                        <p className="text-xs text-slate-400">
                          {[i.sku && `Cód: ${i.sku}`, i.model && `Modelo: ${i.model}`, `${fmtBRL(unit)} un.`].filter(Boolean).join(" • ")}
                        </p>
                      </div>
                      <span className="font-medium shrink-0">{fmtBRL(unit * i.qty)}</span>
                    </li>
                  );
                })}
              </ul>

              {/* pagamento */}
              <div className="bg-blue-50/60 rounded-lg p-4 space-y-1">
                <p className="font-semibold text-gray-900">{detail.payment_label || "Pagamento na entrega"}</p>
                {detail.combined?.length === 2 && (
                  <div className="text-xs text-slate-600 space-y-0.5">
                    {detail.combined.map((c, i) => (
                      <p key={i}>• {c.method === "credito" ? "Cartão de crédito" : c.method === "debito" ? "Cartão de débito" : c.method === "pix" ? "Pix" : "Dinheiro"}: {fmtBRL(c.amount)}</p>
                    ))}
                  </div>
                )}
                {detail.installments > 1 && detail.installment_values?.length > 0 && (
                  <p className="text-xs text-slate-600">
                    {detail.installments}x de {fmtBRL(detail.installment_values[0])}
                    {detail.installment_values[detail.installment_values.length - 1] !== detail.installment_values[0] &&
                      ` (última: ${fmtBRL(detail.installment_values[detail.installment_values.length - 1])})`}
                  </p>
                )}
                <div className="pt-2 space-y-1 border-t border-blue-100">
                  <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{fmtBRL(detail.subtotal)}</span></div>
                  <div className="flex justify-between text-slate-600"><span>Entrega ({detail.region_name})</span><span>{fmtBRL(detail.delivery_fee)}</span></div>
                  <div className="flex justify-between font-heading font-bold text-gray-900 text-base"><span>Total</span><span>{fmtBRL(detail.total)}</span></div>
                </div>
              </div>

              {/* observações internas */}
              <div className="bg-yellow-50/60 border border-yellow-100 rounded-lg p-4" data-testid="order-internal-notes">
                <p className="font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
                  <StickyNote className="w-4 h-4 text-yellow-600" /> Observações internas (só a equipe vê)
                </p>
                {(detail.internal_notes || []).length > 0 ? (
                  <ul className="space-y-1.5 mb-3">
                    {detail.internal_notes.map((n, i) => (
                      <li key={i} className="text-xs text-slate-600">
                        <span className="text-slate-400">{fmtDateTime(n.at)} — {n.by}:</span> {n.text}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-slate-400 mb-3">Nenhuma observação registrada.</p>
                )}
                <div className="flex gap-2">
                  <Input
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addNote()}
                    placeholder="Ex: cliente pediu entrega de manhã..."
                    className="h-9 text-sm bg-white"
                    data-testid="order-note-input"
                  />
                  <Button size="sm" onClick={addNote} disabled={savingNote || !noteText.trim()} data-testid="order-note-add">
                    {savingNote ? "..." : "Registrar"}
                  </Button>
                </div>
              </div>

              {/* histórico */}
              {detail.status_history?.length > 0 && (
                <div>
                  <p className="font-semibold text-gray-900 mb-2">Histórico de status</p>
                  <ul className="space-y-1.5">
                    {detail.status_history.map((h, i) => (
                      <li key={i} className="text-xs text-slate-500 flex justify-between gap-3">
                        <span>
                          {STATUS[h.status]?.label || h.status}
                          {h.reason ? <span className="block text-slate-400">Motivo: {h.reason}</span> : null}
                        </span>
                        <span className="shrink-0">{fmtDateTime(h.at)} • {h.by === "loja" ? "Loja" : h.by === "cliente" ? "Cliente" : h.by}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* registro de operações (arquivamento, teste, exclusão etc.) */}
              {detail.audit?.length > 0 && (
                <div data-testid="order-audit">
                  <p className="font-semibold text-gray-900 mb-2">Registro de operações</p>
                  <ul className="space-y-1.5">
                    {detail.audit.map((a, i) => (
                      <li key={i} className="text-xs text-slate-500 flex justify-between gap-3">
                        <span>
                          {a.action}
                          {a.detail ? <span className="block text-slate-400">{a.detail}</span> : null}
                        </span>
                        <span className="shrink-0">{fmtDateTime(a.at)} • {a.by}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* notificações */}
              <div
                className={`flex items-center justify-between gap-3 rounded-lg p-3 ${
                  notifFailed(detail) ? "bg-red-50" : "bg-slate-50"
                }`}
                data-testid="order-notification-status"
              >
                <p className={`text-xs ${notifFailed(detail) ? "text-red-600 font-medium" : "text-slate-500"}`}>
                  {detail.notifications?.length
                    ? lastNotif(detail).ok
                      ? `Notificação enviada (${lastNotif(detail).detail})`
                      : notifFailed(detail)
                        ? `Falha no envio da notificação: ${lastNotif(detail).detail} — o pedido segue salvo aqui no painel.`
                        : lastNotif(detail).detail
                    : "Nenhuma notificação enviada."}
                </p>
                <Button variant="outline" size="sm" onClick={() => retryNotify(detail.id)} data-testid="order-notify-retry">
                  <BellRing className="w-3.5 h-3.5" /> Reenviar
                </Button>
              </div>

              <a
                href={buildWhatsAppUrl(`Olá ${detail.customer.name}! Sobre seu pedido ${detail.order_number || ""} na HG Móveis...`, settings.whatsapp_number)}
                target="_blank" rel="noreferrer" className="block"
              >
                <Button className="w-full rounded-full bg-green-600 hover:bg-green-700" data-testid="order-whatsapp-customer">
                  <MessageCircle className="w-4 h-4" /> Chamar cliente no WhatsApp
                </Button>
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* cancelamento pelo admin: motivo obrigatório + aviso ao cliente */}
      <Dialog open={!!pendingCancel} onOpenChange={() => setPendingCancel(null)}>
        <DialogContent className="max-w-md" data-testid="cancel-order-dialog">
          <DialogHeader>
            <DialogTitle>Cancelar pedido {pendingCancel?.label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div>
              <Label>Motivo do cancelamento (obrigatório)</Label>
              <Input
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Ex: cliente desistiu, sem estoque..."
                className="mt-1.5"
                data-testid="cancel-reason-input"
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={notifyCustomer}
                onChange={(e) => setNotifyCustomer(e.target.checked)}
                className="accent-[#0B5394]"
                data-testid="cancel-notify-customer"
              />
              Abrir WhatsApp para informar o cliente sobre o cancelamento
            </label>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setPendingCancel(null)} data-testid="cancel-abort">
                Voltar
              </Button>
              <Button
                onClick={confirmCancel}
                disabled={!cancelReason.trim()}
                className="bg-red-600 hover:bg-red-700"
                data-testid="cancel-confirm"
              >
                Confirmar cancelamento
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* exclusão de pedido de TESTE: confirmação obrigatória */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="max-w-md" data-testid="delete-order-dialog">
          <DialogHeader>
            <DialogTitle>Excluir pedido {confirmDelete?.order_number || ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="font-semibold text-gray-900">Tem certeza de que deseja excluir este pedido?</p>
            <p className="text-slate-600">
              O pedido sairá da listagem e uma <strong>cópia de auditoria</strong> será preservada no sistema
              (quem excluiu e quando). Pedidos reais nunca são excluídos — use "Arquivar" para organizá-los.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirmDelete(null)} data-testid="delete-abort">
                Voltar
              </Button>
              <Button
                onClick={deleteTestOrder}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
                data-testid="delete-confirm"
              >
                {deleting ? "Excluindo..." : "Sim, excluir este teste"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* confirmação de exclusão em lote (somente testes) */}
      <Dialog open={confirmBulk} onOpenChange={setConfirmBulk}>
        <DialogContent className="max-w-md" data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle>Excluir {testSelected.length} pedido(s) de teste</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="font-semibold text-gray-900">Tem certeza de que deseja excluir estes pedidos?</p>
            <ul className="max-h-40 overflow-y-auto text-slate-600 space-y-1">
              {testSelected.map((id) => {
                const o = orders.find((x) => x.id === id);
                return <li key={id}>• {o?.order_number || id} — {o?.customer?.name} ({fmtBRL(o?.total || 0)})</li>;
              })}
            </ul>
            <p className="text-slate-600">
              Cópias de auditoria serão preservadas (quem excluiu e quando). Nenhum cliente, produto ou pedido real é afetado.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirmBulk(false)} data-testid="bulk-delete-abort">
                Voltar
              </Button>
              <Button onClick={bulkDelete} disabled={bulkDeleting} className="bg-red-600 hover:bg-red-700" data-testid="bulk-delete-confirm">
                {bulkDeleting ? "Excluindo..." : `Sim, excluir ${testSelected.length} teste(s)`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
